import { useCallback, useMemo } from "react";
import {
  City,
  Country,
  openUrl,
  RegisterReq,
  useRegister,
  useServerForm,
} from "../services";
import {
  Button,
  Col,
  Form,
  Input,
  PickerInput,
  TextLabel,
  TextLink,
  TextInput,
} from "../lib";
import { CountryInput } from "./country-input";

const initialValues: RegisterReq = {
  city_id: 1,
  country_code: "TH",
  country_id: 764,
  email: "",
  email_confirmation: "",
  name: "",
  phone_national: "",
};

type Props = {
  cities: City[];
  countries: Country[];
  onSuccess?: (phone: string) => void;
};

export function RegisterForm({ cities, countries, onSuccess }: Props) {
  // Vars
  const [register, isRegistering, error] = useRegister();
  const { values, setValue, setValues } = useServerForm(initialValues, error);
  const isDisabled =
    !values.city_id ||
    !values.country_code ||
    !values.country_id ||
    !values.email ||
    !values.email_confirmation ||
    !values.name ||
    !values.phone_national;

  const cityOptions = useMemo(() => {
    return cities.map((city) => ({
      label: city.name,
      value: city.id,
    }));
  }, [cities]);

  // Handlers
  const handleChangeCity = useCallback(
    (_name: "city_id", cityId: number | null) => {
      // TOFIX: @react-native-picker/picker seems to always return a string here
      const _cityId = typeof cityId === "string" ? Number(cityId) : cityId;

      const city = cities.find(({ id }) => id === _cityId);

      if (city) {
        const country = countries.find(({ id }) => id === city.country_id);

        if (country) {
          setValues({
            country_id: country.id,
            country_code: country.code,
            city_id: city.id,
          });
        }
      }
    },
    [cities, countries, setValues],
  );

  const handleChangeCountry = useCallback(
    (country: Country) => {
      setValues({
        country_id: country.id,
        country_code: country.code,
      });
    },
    [setValues],
  );

  const handleSubmit = useCallback(async () => {
    const res = await register(values);

    if (res) {
      onSuccess?.(res.phone);
    }
  }, [register, values, onSuccess]);

  const openPrivacy = useCallback(() => {
    openUrl("https://smooci.com/privacy");
  }, []);

  const openTerms = useCallback(() => {
    openUrl("https://smooci.com/terms");
  }, []);

  // Render
  return (
    <Form small>
      <Input label="Nickname">
        <TextInput
          name="name"
          placeholder="e.g. Maria"
          value={values.name}
          onChange={setValue}
        />
      </Input>
      <Input label="E-mail">
        <TextInput
          name="email"
          value={values.email}
          inputMode="email"
          placeholder="e.g. alice@gmail.com"
          onChange={setValue}
          autoCapitalize="none"
          autoCorrect={false}
        />
        <Col mt="2">
          <TextInput
            name="email_confirmation"
            value={values.email_confirmation}
            inputMode="email"
            placeholder="Confirm e-mail address"
            onChange={setValue}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </Col>
      </Input>

      <Input label="City">
        <PickerInput
          name="city_id"
          value={values.city_id}
          placeholder="Select city..."
          options={cityOptions}
          onChange={handleChangeCity}
        />
      </Input>

      <Input label="Phone">
        <CountryInput
          onChange={handleChangeCountry}
          value={values.country_id}
          variant="phone"
        />
        <Col mt="2">
          <TextInput
            name="phone_national"
            value={values.phone_national}
            inputMode="tel"
            placeholder="Phone number (e.g. 0955443311)"
            onChange={setValue}
            returnKeyType="done"
          />
        </Col>
      </Input>

      <Button
        disabled={isDisabled}
        em
        onPress={handleSubmit}
        loading={isRegistering}
      >
        Sign up
      </Button>

      <TextLabel center mt="6">
        By signing up you agree to{" "}
        <TextLink onPress={openPrivacy}>Privacy policy</TextLink> and{" "}
        <TextLink onPress={openTerms}>Terms and conditions</TextLink>
      </TextLabel>
    </Form>
  );
}
